import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import {
  _UiLinePath,
  _UiLinePoint,
  UiLineChartCategory,
  UiLineChartCurve,
  UiLineChartLabelMode,
  UiLineChartPointEvent,
  UiLineChartSeries,
  UiLineChartShowDots,
  UiLineChartVariant,
} from './line-chart.types';

/** @internal Tick del eje Y */
interface YTick { value: number; label: string; y: number; }
/** @internal Tick del eje X */
interface XTick { label: string; x: number; }
/** @internal Fila del tooltip */
interface TooltipEntry { color: string; seriesLabel: string; value: number; }

const DEFAULT_PALETTE = [
  '#4169e2', '#f97316', '#22c55e', '#8b5cf6',
  '#eab308', '#ef4444', '#06b6d4', '#7b96f5',
];

/**
 * Organismo: gráfico de línea presentacional.
 *
 * Implementado con SVG nativo — sin dependencias de charting externas.
 * Variantes: line · area · stacked-area · stepped · sparkline.
 * Curvas: smooth (Catmull-Rom) · linear · stepped.
 *
 * @example
 * <ui-line-chart
 *   [series]="mySeries"
 *   [categories]="myCategories"
 *   variant="area"
 *   (pointClick)="onPoint($event)"
 * />
 */
@Component({
  selector: 'ui-line-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
})
export class UiLineChartComponent {
  // ── Inputs ──────────────────────────────────────────────────────────────
  readonly series       = input<UiLineChartSeries[]>([]);
  readonly categories   = input<UiLineChartCategory[]>([]);
  readonly variant      = input<UiLineChartVariant>('line');
  readonly width        = input<number>(480);
  readonly height       = input<number>(280);
  readonly curve        = input<UiLineChartCurve>('smooth');
  readonly lineWidth    = input<number>(2.5);
  readonly dotRadius    = input<number>(4);
  readonly showDots     = input<UiLineChartShowDots>('hover');
  readonly showArea     = input<boolean>(false);
  readonly areaOpacity  = input<number>(0.12);
  readonly showGrid     = input<boolean>(true);
  readonly showLegend   = input<boolean>(true);
  readonly title        = input<string>('');
  readonly yLabel       = input<string>('');
  readonly xLabel       = input<string>('');
  readonly labelMode    = input<UiLineChartLabelMode>('tooltip-only');
  readonly loading      = input<boolean>(false);
  readonly animated     = input<boolean>(true);

  // ── Outputs ─────────────────────────────────────────────────────────────
  readonly pointClick = output<UiLineChartPointEvent>();
  readonly pointHover = output<UiLineChartPointEvent | null>();

  // ── Estado UI ────────────────────────────────────────────────────────────
  readonly hoveredCatIdx  = signal<number | null>(null);
  readonly activeKey      = signal<string | null>(null);
  readonly tooltipVisible = signal(false);
  readonly tooltipX       = signal(0);
  readonly tooltipY       = signal(0);

  // ── Márgenes adaptativos ─────────────────────────────────────────────────
  readonly margin = computed(() =>
    this.variant() === 'sparkline'
      ? { top: 6, right: 8, bottom: 6, left: 8 }
      : { top: 20, right: 20, bottom: 48, left: 52 }
  );

  readonly plotW = computed(() => this.width()  - this.margin().left - this.margin().right);
  readonly plotH = computed(() => this.height() - this.margin().top  - this.margin().bottom);

  // ── Dominio Y ────────────────────────────────────────────────────────────
  readonly yDomain = computed<{ min: number; max: number }>(() => {
    const s = this.series();
    if (!s.length) return { min: 0, max: 100 };
    const v = this.variant();

    if (v === 'stacked-area') {
      const n = this.categories().length || (s[0]?.data.length ?? 0);
      const sums = Array.from({ length: n }, (_, i) =>
        s.reduce((acc, sr) => acc + Math.max(0, sr.data[i] ?? 0), 0)
      );
      return { min: 0, max: Math.max(...sums) || 100 };
    }

    const all = s.flatMap(sr => sr.data);
    return { min: Math.min(0, ...all), max: Math.max(0, ...all) || 100 };
  });

  // ── Ticks ────────────────────────────────────────────────────────────────
  readonly yTicks = computed<YTick[]>(() => {
    if (this.variant() === 'sparkline') return [];
    const { min, max } = this.yDomain();
    return niceLinear(min, max, 5).map(v => ({
      value: v,
      label: formatNum(v),
      y: this.valToY(v),
    }));
  });

  readonly xTicks = computed<XTick[]>(() => {
    if (this.variant() === 'sparkline') return [];
    return this.categories().map((c, i) => ({
      label: c.label,
      x: this.catX(i),
    }));
  });

  // ── Puntos calculados por serie ──────────────────────────────────────────
  readonly seriesPoints = computed<_UiLinePoint[][]>(() => {
    const s    = this.series();
    const cats = this.categories();
    const n    = cats.length;
    if (!s.length || !n) return [];

    if (this.variant() === 'stacked-area') {
      const cum = Array<number>(n).fill(0);
      return s.map((sr, si) =>
        Array.from({ length: n }, (_, ci) => {
          cum[ci] += sr.data[ci] ?? 0;
          return {
            x: this.catX(ci), y: this.valToY(cum[ci]),
            value: cum[ci], rawValue: sr.data[ci] ?? 0,
            seriesIdx: si, catIdx: ci,
          } as _UiLinePoint;
        })
      );
    }

    return s.map((sr, si) =>
      Array.from({ length: n }, (_, ci) => {
        const val = sr.data[ci] ?? 0;
        return {
          x: this.catX(ci), y: this.valToY(val),
          value: val, rawValue: val,
          seriesIdx: si, catIdx: ci,
        } as _UiLinePoint;
      })
    );
  });

  /** Línea base de cada serie en stacked-area (punto anterior o eje 0) */
  readonly stackedBaselines = computed<{ x: number; y: number }[][]>(() => {
    const s = this.series();
    const n = this.categories().length;
    if (!n || !s.length) return [];
    const base0 = Array.from({ length: n }, (_, ci) => ({
      x: this.catX(ci), y: this.valToY(0),
    }));
    const pts = this.seriesPoints();
    return s.map((_, si) => (si === 0 ? base0 : pts[si - 1]));
  });

  // ── Paths SVG ────────────────────────────────────────────────────────────
  readonly linePaths = computed<_UiLinePath[]>(() => {
    const s    = this.series();
    const v    = this.variant();
    const pal  = this.pal(s);
    const pts  = this.seriesPoints();
    const cr   = this.curve();
    const hasArea = this.showArea() || v === 'area' || v === 'stacked-area';

    return s.map((sr, si) => {
      const points = pts[si] ?? [];
      const xy = points.map(p => ({ x: p.x, y: p.y }));

      const linePath =
        v === 'stepped'
          ? steppedPath(xy)
          : cr === 'smooth'
            ? catmullRomPath(xy)
            : linearPath(xy);

      let areaPath = '';
      if (hasArea && points.length) {
        if (v === 'stacked-area') {
          const basePts = this.stackedBaselines()[si] ?? [];
          areaPath = buildStackedAreaPath(linePath, xy, basePts);
        } else {
          areaPath = buildAreaPath(linePath, xy, this.valToY(0));
        }
      }

      const color = sr.color ?? pal[si % pal.length];
      return { linePath, areaPath, strokeColor: color, fillColor: color, seriesIdx: si };
    });
  });

  readonly allPoints = computed<_UiLinePoint[]>(() => this.seriesPoints().flat());

  readonly hoveredPoints = computed<_UiLinePoint[]>(() => {
    const ci = this.hoveredCatIdx();
    return ci !== null ? this.allPoints().filter(p => p.catIdx === ci) : [];
  });

  readonly crosshairX = computed<number | null>(() => {
    const ci = this.hoveredCatIdx();
    return ci !== null ? this.catX(ci) : null;
  });

  readonly tooltipData = computed<{ label: string; entries: TooltipEntry[] } | null>(() => {
    const ci = this.hoveredCatIdx();
    if (ci === null) return null;
    const cat = this.categories()[ci];
    if (!cat) return null;
    const s = this.series();
    return {
      label: cat.label,
      entries: this.hoveredPoints().map(pt => ({
        color: s[pt.seriesIdx]?.color ?? DEFAULT_PALETTE[pt.seriesIdx % DEFAULT_PALETTE.length],
        seriesLabel: s[pt.seriesIdx]?.label ?? '',
        value: pt.rawValue,
      })),
    };
  });

  readonly zeroY = computed(() => this.valToY(0));

  // ── Event handlers ────────────────────────────────────────────────────────
  onCatEnter(ci: number): void {
    this.hoveredCatIdx.set(ci);
    this.tooltipVisible.set(true);
  }

  onCanvasLeave(): void {
    this.hoveredCatIdx.set(null);
    this.tooltipVisible.set(false);
    this.pointHover.emit(null);
  }

  onMouseMove(event: MouseEvent): void {
    const wrap = event.currentTarget as HTMLElement;
    const rect = wrap.getBoundingClientRect();
    this.tooltipX.set(event.clientX - rect.left + 16);
    this.tooltipY.set(event.clientY - rect.top - 56);
  }

  onPointClick(pt: _UiLinePoint): void {
    const k = ptKey(pt);
    this.activeKey.set(this.activeKey() === k ? null : k);
    this.pointClick.emit({
      series: this.series()[pt.seriesIdx],
      categoryIndex: pt.catIdx,
      value: pt.rawValue,
    });
    this.pointHover.emit({
      series: this.series()[pt.seriesIdx],
      categoryIndex: pt.catIdx,
      value: pt.rawValue,
    });
  }

  // ── Template helpers ──────────────────────────────────────────────────────
  protected isActive(pt: _UiLinePoint): boolean {
    return this.activeKey() === ptKey(pt);
  }

  protected isHoveredCat(ci: number): boolean {
    return this.hoveredCatIdx() === ci;
  }

  protected dotR(pt: _UiLinePoint): number {
    const base = this.dotRadius();
    if (this.isActive(pt)) return base + 2;
    if (this.isHoveredCat(pt.catIdx)) return base + 3;
    return base;
  }

  protected showDot(pt: _UiLinePoint): boolean {
    const sd = this.showDots();
    const v  = this.variant();
    if (sd === 'never') return false;
    if (sd === 'always') return true;
    if (v === 'sparkline') return pt.catIdx === this.categories().length - 1;
    return this.isHoveredCat(pt.catIdx) || this.isActive(pt);
  }

  /**
   * Delay de aparición del punto sincronizado con el dibujado de la línea.
   * Cada punto aparece cuando la animación de trazo llega a su posición X.
   *
   * Fórmula: delay_serie + fracción_X × duración_línea
   */
  protected dotDelay(pt: _UiLinePoint): string {
    const n          = this.categories().length;
    const fraction   = n > 1 ? pt.catIdx / (n - 1) : 0;
    const lineDelay  = pt.seriesIdx * 0.08;
    const lineDur    = 0.9; // debe coincidir con la duración de ui-line-draw
    return `${(lineDelay + fraction * lineDur).toFixed(3)}s`;
  }

  protected showLabel(pt: _UiLinePoint): boolean {
    const lm = this.labelMode();
    if (lm !== 'top') return false;
    if (this.variant() === 'sparkline') return false;
    return true;
  }

  protected seriesColor(idx: number): string {
    return this.series()[idx]?.color ?? DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
  }

  protected areaFill(lp: _UiLinePath): string {
    const v = this.variant();
    return v === 'stacked-area'
      ? lp.fillColor
      : `url(#ui-line-grad-${lp.seriesIdx})`;
  }

  protected areaFillOpacity(lp: _UiLinePath): number {
    const v    = this.variant();
    const base = v === 'stacked-area' ? 0.45 : 1;
    const ci   = this.hoveredCatIdx();
    const hov  = ci !== null && this.hoveredPoints().some(p => p.seriesIdx === lp.seriesIdx);
    return v === 'stacked-area'
      ? (hov ? Math.min(0.7, base + 0.15) : base)
      : 1;
  }

  protected colHitBounds(ci: number): { x: number; width: number } {
    const n  = this.categories().length;
    const pw = this.plotW();
    if (n <= 1) return { x: 0, width: pw };
    const step  = pw / (n - 1);
    const left  = ci === 0     ? 0  : this.catX(ci) - step / 2;
    const right = ci === n - 1 ? pw : this.catX(ci) + step / 2;
    return { x: left, width: right - left };
  }

  protected gradStopOpacity(lp: _UiLinePath): number {
    return Math.min(1, this.areaOpacity() * 2.5);
  }

  // ── Helpers de geometría ──────────────────────────────────────────────────
  protected catX(i: number): number {
    const n = this.categories().length;
    const w = this.plotW();
    if (n <= 1) return w / 2;
    return i * (w / (n - 1));
  }

  protected valToY(val: number): number {
    const { min, max } = this.yDomain();
    return this.plotH() - ((val - min) / (max - min)) * this.plotH();
  }

  private pal(s: UiLineChartSeries[]): string[] {
    return s.map((sr, i) => sr.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]);
  }
}

// ── Utilidades puras ──────────────────────────────────────────────────────────

function ptKey(p: _UiLinePoint): string {
  return `${p.seriesIdx}-${p.catIdx}`;
}

function catmullRomPath(pts: { x: number; y: number }[]): string {
  if (!pts.length) return '';
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
  if (pts.length === 2) return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
  const d = [`M ${pts[0].x} ${pts[0].y}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d.push(`C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2.x} ${p2.y}`);
  }
  return d.join(' ');
}

function linearPath(pts: { x: number; y: number }[]): string {
  if (!pts.length) return '';
  return `M ${pts[0].x} ${pts[0].y}` +
    pts.slice(1).map(p => ` L ${p.x} ${p.y}`).join('');
}

function steppedPath(pts: { x: number; y: number }[]): string {
  if (!pts.length) return '';
  const d = [`M ${pts[0].x} ${pts[0].y}`];
  for (let i = 1; i < pts.length; i++) {
    d.push(`H ${pts[i].x} V ${pts[i].y}`);
  }
  return d.join(' ');
}

function buildAreaPath(
  linePath: string,
  pts: { x: number; y: number }[],
  baseY: number,
): string {
  if (!pts.length) return '';
  const last  = pts[pts.length - 1];
  const first = pts[0];
  return `${linePath} L ${last.x} ${baseY} L ${first.x} ${baseY} Z`;
}

function buildStackedAreaPath(
  topLinePath: string,
  _topPts: { x: number; y: number }[],
  botPts: { x: number; y: number }[],
): string {
  if (!botPts.length) return '';
  const lastBot  = botPts[botPts.length - 1];
  const backPath = [...botPts].reverse().map(p => `L ${p.x} ${p.y}`).join(' ');
  return `${topLinePath} L ${lastBot.x} ${lastBot.y} ${backPath} Z`;
}

function niceLinear(min: number, max: number, count: number): number[] {
  if (min === max) return [min];
  const range = max - min;
  const step  = niceStep(range / count);
  const start = Math.floor(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.01; v = +(v + step).toFixed(10)) {
    ticks.push(+v.toFixed(10));
    if (ticks.length > count + 2) break;
  }
  return ticks;
}

function niceStep(rough: number): number {
  const exp  = Math.floor(Math.log10(rough));
  const frac = rough / Math.pow(10, exp);
  const nice = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10;
  return nice * Math.pow(10, exp);
}

function formatNum(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}k`;
  return String(v % 1 === 0 ? v : v.toFixed(1));
}
